<script setup lang="ts">
  // Tips stats migrated to the V2 design system: total / per-payment-method
  // modes + shared series report body.
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import StatsService from '@/api/services/stats.service';
  import V2ReportPage from '@/components/v2/stats/V2ReportPage.vue';
  import V2StatsDateRange from '@/components/v2/stats/V2StatsDateRange.vue';
  import V2StatsIntervalSelect from '@/components/v2/stats/V2StatsIntervalSelect.vue';
  import { TStatsChartInterval } from '@/components/v2/stats/v2-stats-interval.types';
  import V2StatsScopeNote from '@/components/v2/stats/V2StatsScopeNote.vue';
  import V2StatsSeriesReport from '@/components/v2/stats/V2StatsSeriesReport.vue';
  import { useStatsSeriesDisplay } from '@/composition/use-stats-series-display';
  import { useUi } from '@/composition/use-ui';
  import { IStatsDateRange } from '@/domain/stats/stats-date-range';
  import { IStats } from '@/interfaces/IStats';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { buildChartLabels } from '@/statsutils';

  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }

  const currencySymbol = computed(
    () => restaurantStore.currency?.symbol ?? ''
  );

  //#region Filters

  const range = ref<IStatsDateRange | null>(null);
  const interval = ref<TStatsChartInterval>('auto');
  const mode = ref<'total' | 'payment'>('total');

  function onRangeChange(value: IStatsDateRange) {
    range.value = value;
    load();
  }

  function setMode(value: 'total' | 'payment') {
    if (mode.value === value) return;
    mode.value = value;
  }

  watch([interval, mode], () => {
    load();
  });

  //#endregion

  //#region Loading

  const statsData = ref<IStats | null>(null);
  const loading = ref(false);

  const SERIES_LABEL_KEYS: Record<string, string> = {
    total: 'v2Stats.series.total',
    count: 'v2Stats.series.count',
    average: 'v2Stats.series.avgPerTip',
    cash: 'v2Stats.series.cash',
    card: 'v2Stats.series.card',
    online: 'v2Stats.series.online',
    banktransfer: 'v2Stats.series.banktransfer',
    qr: 'v2Stats.series.qr',
    invitation: 'v2Stats.series.invitation',
    employee: 'v2Stats.series.employee',
    customer: 'v2Stats.series.customerAccount',
  };

  const SERIES_COLORS = [
    '#45a5f5',
    '#30d565',
    '#f5e938',
    '#f56041',
    '#f5418a',
    '#c44ff5',
    '#6451f5',
    '#00b8b8',
  ];

  let requestGeneration = 0;

  async function load() {
    const restaurant = restaurantStore.restaurant;
    const branch = restaurantStore.selectedBranch;
    if (!restaurant || !branch || !range.value) return;

    const generation = ++requestGeneration;
    loading.value = true;
    try {
      const stats = await StatsService.getTipsStats(
        restaurant.id,
        branch.id,
        range.value.from,
        range.value.to,
        mode.value,
        undefined,
        interval.value,
        range.value.calendar
      );
      if (generation !== requestGeneration) return;

      stats.labels = buildChartLabels(stats);
      statsData.value = stats;
    } catch (error) {
      if (generation !== requestGeneration) return;
      statsData.value = null;
      ui.serverError(error, i18nT('v2Stats.tips.loadError'));
    } finally {
      if (generation === requestGeneration) loading.value = false;
    }
  }

  const displayStats = useStatsSeriesDisplay({
    stats: statsData,
    labelKeys: SERIES_LABEL_KEYS,
    colors: SERIES_COLORS,
    currencySymbol,
  });

  //#endregion
</script>

<template>
  <V2ReportPage
    :title="String($t('v2Stats.tips.title'))"
    :subtitle="restaurantStore.selectedBranch?.name"
  >
    <template #range>
      <V2StatsIntervalSelect :value="interval" @input="interval = $event" />
      <V2StatsDateRange
        v-if="restaurantStore.restaurant && restaurantStore.selectedBranch"
        :restaurant-id="restaurantStore.restaurant.id"
        :branch-id="restaurantStore.selectedBranch.id"
        storage-key="tips"
        @change="onRangeChange"
      />
    </template>

    <template #note>
      <V2StatsScopeNote
        storage-key="tips"
        :text="String($t('statsNote.tips'))"
      />
    </template>

    <div class="mode-toggle">
      <button
        type="button"
        class="mode-option"
        :class="{ 'is-active': mode === 'total' }"
        @click="setMode('total')"
      >
        {{ $t('v2Stats.tips.modeTotal') }}
      </button>
      <button
        type="button"
        class="mode-option"
        :class="{ 'is-active': mode === 'payment' }"
        @click="setMode('payment')"
      >
        {{ $t('v2Stats.tips.modePayment') }}
      </button>
    </div>

    <V2StatsSeriesReport
      :stats="displayStats"
      :loading="loading"
      chart-type="line"
      :chart-title="String($t('v2Stats.tips.chartTitle'))"
      :currency-symbol="currencySymbol"
      export-filename="tips-stats"
      :export-sheet="String($t('v2Stats.tips.title'))"
      :first-column-label="String($t('v2Stats.colDate'))"
    />
  </V2ReportPage>
</template>

<style scoped lang="scss">
  .mode-toggle {
    @apply tw-inline-flex tw-rounded-lg tw-bg-neutral-100 tw-p-0.5;

    .mode-option {
      // h-10 options + track padding = 44px total hit area.
      @apply tw-h-10 tw-whitespace-nowrap tw-rounded-md tw-border-0 tw-bg-transparent tw-px-3 tw-text-sm tw-font-medium tw-text-neutral-500 tw-transition-colors;

      &.is-active {
        @apply tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-text-neutral-900 tw-shadow-sm;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
  }
</style>
